"use client"

import * as React from "react"

import { ConfettiBurst } from "@/components/ui/confetti-burst"
import { useAmbientCount } from "@/lib/hooks/use-ambient-count"
import { prizeById } from "@/lib/wheel/prizes"
import {
  TOP_PRIZES,
  relativeTime,
  selectBigDrop,
  selectBigKey,
  selectDropRows,
  useDropFeed,
  type DropRow,
} from "@/lib/wheel/drop-feed"
import { cn } from "@/lib/utils"

/* The right rail of /wheel: what the floor is doing while the reader stands
   at the wheel. Reads the shared drop feed (lib/wheel/drop-feed); the page
   starts it. */

export interface HistoryRow {
  id: string
  prizeId: string
  at: number
}

export interface WheelAmbientProps {
  /** The reader's own spins, merged on top as "you". */
  history: HistoryRow[]
  className?: string
}

export function useMergedDrops(history: HistoryRow[]): DropRow[] {
  const rows = useDropFeed(selectDropRows)
  return React.useMemo(() => {
    const own: DropRow[] = history.map((h) => ({
      id: `you-${h.id}`,
      wallet: "you",
      prizeId: h.prizeId,
      at: h.at,
    }))
    return [...own, ...rows].sort((a, b) => b.at - a.at)
  }, [history, rows])
}

export function WheelAmbient({ history, className }: WheelAmbientProps) {
  const [now, setNow] = React.useState(() => Date.now())
  const { displayed: spinsToday } = useAmbientCount(1180, {
    minIntervalMs: 4000,
    maxIntervalMs: 9000,
    bumpFn: () => 1 + Math.floor(Math.random() * 3),
  })

  React.useEffect(() => {
    const clock = window.setInterval(() => setNow(Date.now()), 5000)
    return () => window.clearInterval(clock)
  }, [])

  const merged = useMergedDrops(history)
  const visible = merged.slice(0, 8)
  const leaving = merged[8]

  // A big drop celebrates in its own row and in the tab title, nowhere else.
  const big = useDropFeed(selectBigDrop)
  const bigKey = useDropFeed(selectBigKey)
  const [hot, setHot] = React.useState<{ id: string; key: number } | null>(null)
  React.useEffect(() => {
    if (!big || bigKey === 0) return
    setHot({ id: big.id, key: bigKey })
    const was = document.title
    document.title = `${big.wallet} hit ${prizeById(big.prizeId).label}`
    const t = window.setTimeout(() => {
      setHot(null)
      document.title = was
    }, 3400)
    return () => {
      window.clearTimeout(t)
      document.title = was
    }
  }, [big, bigKey])

  return (
    <aside className={cn("flex flex-col gap-8", className)}>
      <section>
        <h2 className="font-open-sauce text-sm font-semibold text-white">Recent drops</h2>
        <ul className="mt-2">
          {visible.map((row, i) => (
            <li
              key={row.id}
              className={cn(
                "relative flex items-center gap-3 overflow-hidden border-b border-white-100 py-2 font-open-sauce text-sm tabular-nums",
                i === 0 && row.id.startsWith("drop-") && "wheel-row-in"
              )}
            >
              {hot?.id === row.id && (
                <span key={hot.key} aria-hidden className="pointer-events-none absolute inset-0">
                  <span
                    className="wheel-flash absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(90deg, hsl(var(--primary-0) / 0.35) 0%, hsl(var(--primary-0) / 0.10) 60%, transparent 100%)",
                    }}
                  />
                  <span className="promo-shimmer-sweep absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,hsl(0_0%_100%/0.35)_50%,transparent_100%)]" />
                  <ConfettiBurst spread={110} delayMs={60} />
                </span>
              )}
              <span
                className={cn(
                  "w-[7.5rem] shrink-0 truncate",
                  row.wallet === "you" ? "text-white" : "text-grey-300"
                )}
              >
                {row.wallet}
              </span>
              <span
                className={cn(
                  "flex-1 truncate font-semibold",
                  TOP_PRIZES.has(row.prizeId) ? "text-primary" : "text-white"
                )}
              >
                {prizeById(row.prizeId).label}
              </span>
              <span className="shrink-0 text-grey-400">{relativeTime(row.at, now)}</span>
            </li>
          ))}
          {leaving && (
            <li
              key={leaving.id}
              aria-hidden
              className="promo-fade-out flex items-center gap-3 py-2 font-open-sauce text-sm tabular-nums text-grey-400"
            >
              <span className="w-[7.5rem] shrink-0 truncate">{leaving.wallet}</span>
              <span className="flex-1 truncate">{prizeById(leaving.prizeId).label}</span>
              <span className="shrink-0">{relativeTime(leaving.at, now)}</span>
            </li>
          )}
        </ul>
      </section>

      <section>
        <h2 className="font-open-sauce text-sm font-semibold text-white">Spins today</h2>
        <p className="mt-1 font-open-sauce text-[36px] font-black leading-none tabular-nums text-white">
          {spinsToday.toLocaleString("en-US")}
        </p>
        <p className="mt-1 font-open-sauce text-sm text-grey-400">spins on the floor today</p>
      </section>
    </aside>
  )
}
